/**
 * Menu Permissions Utility
 * Provides all available menu items for permission assignment
 */

import { menuItems, MenuItem } from "../../../layouts/menuConfig";
import { dashboardBoxes } from "./dashboardBoxes";
import { Permission } from "../../../types/permission.types";

// Re-export Permission as MenuPermission for backwards compatibility
export type MenuPermission = Permission;

export interface MenuItemForPermission {
  path: string;
  text: string;
  iconClass: string;
  isParent: boolean;
  parentText?: string;
}

/**
 * Flatten menu items including sub-items
 * Returns ALL menu items for admin, chatbot, and superadmin roles
 */
const flattenMenuItems = (): MenuItemForPermission[] => {
  const items: MenuItemForPermission[] = [];

  menuItems.forEach((item: MenuItem) => {
    // Skip items without a path (parent items with only sub-items)
    if (item.path) {
      items.push({
        path: item.path,
        text: item.text,
        iconClass: item.iconClass,
        isParent: false,
        // Root menu items don't get a parentText, they're shown directly
      });
    }

    // Add sub-items if they exist
    if (item.subItems && item.subItems.length > 0) {
      item.subItems.forEach((subItem: MenuItem) => {
        if (subItem.path) {
          items.push({
            path: subItem.path,
            text: subItem.text,
            iconClass: subItem.iconClass,
            isParent: false,
            parentText: item.text,
          });
        }
      });
    }
  });

  // Add dashboard boxes as sub-items under the dashboard menu item
  dashboardBoxes.forEach((box) => {
    items.push({
      path: `/dashboard/${box.id}`,
      text: box.label,
      iconClass: "fa-solid fa-box",
      isParent: false,
      parentText: "Dashboard",
    });
  });

  return items;
};

/**
 * Get all available menu items for permission assignment
 * Excludes the role management page itself from being assigned to roles
 */
export const getAllMenuItems = (): MenuItemForPermission[] => {
  const items = flattenMenuItems();
  // Filter out role-management as it should not be assignable to roles
  return items.filter(item => item.path !== "/role-management");
};

/**
 * Create default permission object for a menu item
 */
export const createDefaultPermission = (menuPath: string, menuText: string): MenuPermission => {
  return {
    menuPath,
    menuText,
    canView: true,
    canCreate: false,
    canEdit: false,
    canDelete: false,
  };
};

/**
 * Validate permission object
 */
export const validatePermission = (permission: MenuPermission): boolean => {
  if (!permission.menuPath || !permission.menuText) {
    return false;
  }

  // At least canView should be true if any permission is granted
  if (!permission.canView && (permission.canCreate || permission.canEdit || permission.canDelete)) {
    return false;
  }

  return true;
};

/**
 * Group permissions by parent menu
 */
export const groupPermissionsByParent = (permissions: MenuPermission[]): Record<string, MenuPermission[]> => {
  const grouped: Record<string, MenuPermission[]> = {};
  const menuItemsMap = new Map<string, MenuItemForPermission>();

  // Create a map of all menu items
  getAllMenuItems().forEach((item) => {
    menuItemsMap.set(item.path, item);
  });

  permissions.forEach((permission) => {
    const menuItem = menuItemsMap.get(permission.menuPath);
    const parentKey = menuItem?.parentText || "Main Menu";

    if (!grouped[parentKey]) {
      grouped[parentKey] = [];
    }

    grouped[parentKey].push(permission);
  });

  return grouped;
};

/**
 * Check if a menu item supports CRUD operations
 * Returns true if the menu path has any CRUD operations (C/R/U/D)
 */
export const supportsCRUD = (menuPath: string): boolean => {
  // Define which paths support CRUD operations
  // Dashboard and dashboard boxes are view-only
  if (menuPath === "/dashboard" || menuPath.startsWith("/dashboard/")) {
    return false;
  }

  // All other menu items support CRUD operations
  const crudSupportedPaths = [
    // Core pages
    "/users",
    "/admin",
    "/userAdmin",
    "/message-center",
    "/role-management",
    // IMPACT ECK
    "/challenges",
    "/guides",
    // COMMUNITY
    "/news",
    "/post",
    "/survey",
    "/company",
    "/warning",
    "/document",
    // DEIN KEB
    "/service-category",
    "/service-category-items",
    // EINSTELLUNGEN
    "/help",
    "/legal-notice",
    "/terms-and-conditions",
    "/privacy",
    "/about-app",
    // ANDERE
    "/contact",
    "/wreath",
    "/redeem-coins",
    // Special - rating is view + delete only
    "/rating",
  ];

  return crudSupportedPaths.includes(menuPath);
};

/**
 * Get specific CRUD capabilities for a menu path
 * Returns which operations are supported for the given path
 */
export const getCRUDCapabilities = (menuPath: string): { create: boolean; edit: boolean; delete: boolean } => {
  // Rating page only supports view and delete (for deleting ratings)
  if (menuPath === "/rating") {
    return { create: false, edit: false, delete: true };
  }

  // Dashboard and dashboard boxes are view-only
  if (menuPath === "/dashboard" || menuPath.startsWith("/dashboard/")) {
    return { create: false, edit: false, delete: false };
  }

  // All other paths support full CRUD
  return { create: true, edit: true, delete: true };
};

